import { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'

import { listElements } from '../../../api/elements.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'
import { deleteScheduler, listSchedulers, setSchedulerStatus } from '../../../api/schedulers.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

const STATUS_ORDER = {
  Draft: 0,
  Published: 1,
  Archived: 2,
  Deleted: 3,
}

export default function ApplicationSchedulerListPage() {
  const { application, applicationId } = useOutletContext()
  const { confirm, toast } = useFeedback()
  const [filterGroupConfigId, setFilterGroupConfigId] = useState('')
  const [changingStatusId, setChangingStatusId] = useState(null)
  const [state, setState] = useState({
    status: 'loading',
    items: [],
    groupConfigs: [],
    groupConfigsById: {},
    elementsById: {},
    error: null,
  })

  useEffect(() => {
    let cancelled = false

    Promise.all([
      listSchedulers(applicationId),
      listGroupConfigs(applicationId),
      listElements(applicationId),
    ])
      .then(([items, groupConfigs, elements]) => {
        if (cancelled) return
        const sortedItems = [...items].sort((a, b) => {
          const statusDiff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
          if (statusDiff !== 0) return statusDiff
          return String(a.name || '').localeCompare(String(b.name || ''))
        })
        const groupConfigsById = Object.fromEntries(groupConfigs.map((groupConfig) => [String(groupConfig.id), groupConfig]))
        const elementsById = Object.fromEntries(elements.map((element) => [String(element.id), element]))
        setState({
          status: 'ok',
          items: sortedItems,
          groupConfigs: groupConfigs || [],
          groupConfigsById,
          elementsById,
          error: null,
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState((prev) => ({
          ...prev,
          status: 'error',
          items: [],
          groupConfigs: prev.groupConfigs || [],
          groupConfigsById: {},
          elementsById: {},
          error,
        }))
      })

    return () => {
      cancelled = true
    }
  }, [applicationId])

  const filteredItems =
    !filterGroupConfigId || filterGroupConfigId === ''
      ? state.items
      : state.items.filter((s) => String(s.groupConfigId || '') === String(filterGroupConfigId))

  async function handleSetStatus(scheduler, statusValue) {
    try {
      setChangingStatusId(scheduler.id)
      const updated = await setSchedulerStatus(applicationId, scheduler.id, statusValue)
      setState((prev) => ({
        ...prev,
        items: prev.items.map((item) => (String(item.id) === String(scheduler.id) ? updated : item)),
      }))
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.body?.detail || error?.message || error),
      })
    } finally {
      setChangingStatusId(null)
    }
  }

  async function handleDelete(schedulerId) {
    const confirmed = await confirm({
      title: 'Delete scheduler',
      message: 'Delete this scheduler? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteScheduler(applicationId, schedulerId)
      setState((prev) => ({
        ...prev,
        items: prev.items.filter((scheduler) => String(scheduler.id) !== String(schedulerId)),
      }))
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.body?.detail || error?.message || error),
      })
    }
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div>
          <div className="cardTitle">Scheduler</div>
        </div>
        <div className="buttonGroup">
          {state.status === 'ok' && state.groupConfigs.length > 0 ? (
            <select
              className="textInput selectFilter"
              value={filterGroupConfigId}
              onChange={(e) => setFilterGroupConfigId(e.target.value)}
              aria-label="Filter by group config"
            >
              <option value="">All group configs</option>
              {state.groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Group #${gc.id}`}
                </option>
              ))}
            </select>
          ) : null}
          <Link to={`/applications/${applicationId}/scheduler/new`} className="button buttonPrimary">
            Create
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <p className="muted inlineMessage">
          Loading schedulers...
        </p>
      ) : state.status === 'error' ? (
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      ) : state.items.length === 0 ? (
        <p className="muted inlineMessage">
          No schedulers yet.
        </p>
      ) : filteredItems.length === 0 ? (
        <p className="muted inlineMessage">
          No schedulers match the selected filter.
        </p>
      ) : (
        <table className="table" aria-label="Schedulers list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Group config</th>
              <th>Element</th>
              <th>Start date</th>
              <th>End date</th>
              <th>Duration</th>
              <th>Default</th>
              <th>Status</th>
              <th className="alignRight"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((scheduler) => {
              const groupConfig = state.groupConfigsById[String(scheduler.groupConfigId)]
              const element = state.elementsById[String(scheduler.elementId)]
              return (
                <tr key={scheduler.id}>
                  <td>{scheduler.name || 'Untitled'}</td>
                  <td>{groupConfig?.name || scheduler.groupConfigId || ''}</td>
                  <td>{element?.name || scheduler.elementId || ''}</td>
                  <td className="mono">{scheduler.startDate || ''}</td>
                  <td className="mono">{scheduler.endDate || ''}</td>
                  <td>{scheduler.duration ?? ''}</td>
                  <td>{scheduler.isDefault ? 'Yes' : ''}</td>
                  <td>
                    <select
                      className="textInput selectStatusCompact"
                      value={scheduler.status || 'Draft'}
                      onChange={(e) => {
                        const next = e.target.value
                        if (next !== (scheduler.status || 'Draft')) handleSetStatus(scheduler, next)
                      }}
                      disabled={changingStatusId === scheduler.id}
                      aria-label={`Status for ${scheduler.name || 'scheduler'}`}
                    >
                      {(() => {
                        const current = scheduler.status || 'Draft'
                        const options = current === 'Draft' ? ['Draft', 'Published'] : current === 'Published' ? ['Published', 'Archived'] : ['Archived']
                        return options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))
                      })()}
                    </select>
                  </td>
                  <td className="alignRight">
                    <span className="tableActions">
                      <Link
                        to={`/applications/${applicationId}/scheduler/${scheduler.id}`}
                        className="button buttonGhost"
                      >
                        View
                      </Link>
                      {scheduler.status !== 'Published' ? (
                        <button
                          type="button"
                          className="button buttonGhost buttonDanger"
                          onClick={() => handleDelete(scheduler.id)}
                          disabled
                        >
                          Delete
                        </button>
                      ) : (
                        <span className="muted">Protected</span>
                      )}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}
